import { com } from '@eidu/web'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import createPermissionGrant from './api/entity/permission/createPermissionGrant'
import updatePermissionGrant from './api/entity/permission/updatePermissionGrant'
import deletePermissionGrant from './api/entity/permission/deletePermissionGrant'
import { PermissionGrantDTO } from '../domain/permission/PermissionGrantDTO'
import { auditLogKeys, permissionGrantKeys } from './queryKeys'
import PermissionGrantId = com.eidu.entity.permission.PermissionGrantId
import Permission = com.eidu.entity.permission.Permission
import PermissionPolicyId = com.eidu.entity.permission.PermissionPolicyId
import EntitySelector = com.eidu.entity.selector.EntitySelector

type CreateParams = {
  userSelector: EntitySelector
  permissions: readonly Permission[]
  policyIds: readonly PermissionPolicyId[]
}

export const useCreatePermissionGrant = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ userSelector, permissions, policyIds }: CreateParams): Promise<PermissionGrantDTO> =>
      createPermissionGrant({ userSelector, permissions, policyIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: permissionGrantKeys.all })
      queryClient.invalidateQueries({ queryKey: auditLogKeys.all })
    },
  })
}

type ModifyParams = {
  id: PermissionGrantId
  userSelector: EntitySelector
  permissions: readonly Permission[]
  policyIds: readonly PermissionPolicyId[]
}

export const useModifyPermissionGrant = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, userSelector, permissions, policyIds }: ModifyParams): Promise<PermissionGrantDTO> =>
      updatePermissionGrant({ id, userSelector, permissions, policyIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: permissionGrantKeys.all })
      queryClient.invalidateQueries({ queryKey: auditLogKeys.all })
    },
  })
}

export const useDeletePermissionGrant = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: PermissionGrantId): Promise<void> => deletePermissionGrant({ id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: permissionGrantKeys.all })
      queryClient.invalidateQueries({ queryKey: auditLogKeys.all })
    },
  })
}
